// Joystick-driven dials shown at the user's view when a thumbstick crosses
// the deadzone. The left stick brings up the action dial (DRAW / ERASE);
// the right stick brings up the shape dial (BOX / VOXEL / EXTRUDE). Pure
// presentation — the input-driven pollDials lives in main.ts.

import * as THREE from "three";
import {
  type ActionMode,
  type SelectAction,
  type ShapeMode,
} from "../interaction/tool";
import { TEXT_LIGHT, glassFont, setupPanelTexture } from "./glass";
import {
  paintBoxIcon,
  paintCircleIcon,
  paintDeleteIcon,
  paintEraseIcon,
  paintExtrudeIcon,
  paintMoveIcon,
  paintPencilIcon,
  paintVoxelIcon,
} from "./icons";

// Translucent dark disc behind each dial — matches the panel glass.
const DIAL_GLASS = "rgba(34, 36, 46, 0.72)";

const DIAL_RES = 768;
const DIAL_SIZE = 0.22;
// Camera-local offset where dials appear when summoned.
const DIAL_FORWARD = 0.7;
const DIAL_DOWN = 0.18;
// Dot at the center of each dial that tracks raw joystick deflection.
const DIAL_DOT_RADIUS = 0.012;
// Hub radius (matches the canvas drawing's `innerR = W * 0.14`). The dot's
// CENTRE max offset is hub − dot so its outer edge grazes the inner ring at
// full deflection.
const DIAL_HUB_RADIUS = DIAL_SIZE * 0.14;
export const DIAL_DOT_MAX_OFFSET = DIAL_HUB_RADIUS - DIAL_DOT_RADIUS;
const dialDotGeom = new THREE.CircleGeometry(DIAL_DOT_RADIUS, 24);

export interface Dial {
  canvas: HTMLCanvasElement;
  texture: THREE.CanvasTexture;
  group: THREE.Group;
  plane: THREE.Mesh;
  dot: THREE.Mesh;
}

function createDial(dotColor: number): Dial {
  const canvas = document.createElement("canvas");
  canvas.width = DIAL_RES;
  canvas.height = DIAL_RES;
  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  setupPanelTexture(texture);
  const group = new THREE.Group();
  group.visible = false;
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(DIAL_SIZE, DIAL_SIZE),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: false,
    }),
  );
  plane.renderOrder = 950;
  group.add(plane);
  const dot = new THREE.Mesh(
    dialDotGeom,
    new THREE.MeshBasicMaterial({
      color: dotColor,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: false,
    }),
  );
  dot.renderOrder = 952;
  group.add(dot);
  return { canvas, texture, group, plane, dot };
}

// Action dial (DRAW / ERASE), pink theme. Same position as shape dial.
export const actionDial = createDial(0xe55c84);
// Shape dial (BOX / VOXEL / EXTRUDE), blue theme. Same position as action dial.
export const shapeDial = createDial(0x4a90d9);
// Select-action dial (MOVE / DELETE), amber theme. Replaces the shape
// dial on the right joystick when SELECT mode is active.
export const selectActionDial = createDial(0xf4b400);

export function paintActionDial(active: ActionMode): void {
  const W = actionDial.canvas.width;
  const H = actionDial.canvas.height;
  const ctx = actionDial.canvas.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);
  const cx = W / 2;
  const cy = H / 2;
  const outerR = W * 0.48;
  const innerR = W * 0.14;

  // Background disc.
  ctx.fillStyle = DIAL_GLASS;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR + 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(229, 92, 132, 0.85)";
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR + 4, 0, Math.PI * 2);
  ctx.stroke();

  // Two halves: right = DRAW (centred at angle 0), left = ERASE (angle π).
  // Push joystick right → DRAW; push left → ERASE.
  const ACTIONS: {
    mode: ActionMode;
    start: number;
    end: number;
    center: number;
    label: string;
  }[] = [
    { mode: "draw", start: -Math.PI / 2, end: Math.PI / 2, center: 0, label: "DRAW" },
    { mode: "erase", start: Math.PI / 2, end: (3 * Math.PI) / 2, center: Math.PI, label: "ERASE" },
  ];
  const sectionR = (outerR + innerR) / 2;
  const vertOffset = 70;
  const iconSize = 160;
  const labelFontPx = 56;
  // Per-half colour: DRAW reads green, ERASE reads red — so the selected
  // action is obvious at a glance. Hot half is opaque, the other dim.
  const halfRGB = (mode: ActionMode): string =>
    mode === "draw" ? "52, 199, 89" : "229, 70, 70";
  for (const a of ACTIONS) {
    const hot = a.mode === active;
    const rgb = halfRGB(a.mode);
    ctx.fillStyle = hot ? `rgba(${rgb}, 0.92)` : `rgba(${rgb}, 0.22)`;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, a.start, a.end);
    ctx.arc(cx, cy, innerR, a.end, a.start, true);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = `rgba(${rgb}, 0.85)`;
    ctx.lineWidth = 4;
    ctx.stroke();
    const secX = cx + Math.cos(a.center) * sectionR;
    const secY = cy + Math.sin(a.center) * sectionR;
    const iy = secY - vertOffset;
    if (a.mode === "draw") paintPencilIcon(ctx, secX, iy, iconSize);
    else paintEraseIcon(ctx, secX, iy, iconSize);
    const ly = secY + vertOffset;
    ctx.fillStyle = TEXT_LIGHT;
    ctx.font = glassFont(`600 ${labelFontPx}px`);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(a.label, secX, ly);
  }

  // Hub.
  ctx.fillStyle = DIAL_GLASS;
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(229, 92, 132, 0.85)";
  ctx.lineWidth = 4;
  ctx.stroke();

  actionDial.texture.needsUpdate = true;
}

export function paintShapeDial(active: ShapeMode): void {
  const W = shapeDial.canvas.width;
  const H = shapeDial.canvas.height;
  const ctx = shapeDial.canvas.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);
  const cx = W / 2;
  const cy = H / 2;
  const outerR = W * 0.48;
  const innerR = W * 0.14;

  // Background disc.
  ctx.fillStyle = DIAL_GLASS;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR + 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgb(74, 144, 217)";
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR + 4, 0, Math.PI * 2);
  ctx.stroke();

  // Four 90° wedges: top → BOX, right → VOXEL, bottom → CIRCLE, left → EXTRUDE.
  // (Selection thresholds in main's pollDials mirror these screen directions.)
  const SHAPES: { mode: ShapeMode; center: number; label: string }[] = [
    { mode: "box", center: -Math.PI / 2, label: "BOX" },
    { mode: "voxel", center: 0, label: "VOXEL" },
    { mode: "circle", center: Math.PI / 2, label: "CIRCLE" },
    { mode: "extrude", center: Math.PI, label: "EXTRUDE" },
  ];
  // Smaller label font + tighter icon-label spacing so "EXTRUDE" / "CIRCLE"
  // fit comfortably within each 90° wedge without horizontal text bbox
  // spilling into adjacent wedges or the hub.
  const sectionR = (outerR + innerR) / 2;
  const vertOffset = 40;
  const iconSize = 110;
  const labelFontPx = 34;
  for (const s of SHAPES) {
    const start = s.center - Math.PI / 4;
    const end = s.center + Math.PI / 4;
    const hot = s.mode === active;
    ctx.fillStyle = hot
      ? "rgba(74, 144, 217, 0.92)"
      : "rgba(74, 144, 217, 0.22)";
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, start, end);
    ctx.arc(cx, cy, innerR, end, start, true);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(74, 144, 217, 0.85)";
    ctx.lineWidth = 4;
    ctx.stroke();
    const secX = cx + Math.cos(s.center) * sectionR;
    const secY = cy + Math.sin(s.center) * sectionR;
    const iy = secY - vertOffset;
    if (s.mode === "box") paintBoxIcon(ctx, secX, iy, iconSize);
    else if (s.mode === "voxel") paintVoxelIcon(ctx, secX, iy, iconSize);
    else if (s.mode === "circle") paintCircleIcon(ctx, secX, iy, iconSize);
    else paintExtrudeIcon(ctx, secX, iy, iconSize);
    const ly = secY + vertOffset;
    ctx.fillStyle = TEXT_LIGHT;
    ctx.font = glassFont(`600 ${labelFontPx}px`);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(s.label, secX, ly);
  }

  // Hub.
  ctx.fillStyle = DIAL_GLASS;
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgb(74, 144, 217)";
  ctx.lineWidth = 4;
  ctx.stroke();

  shapeDial.texture.needsUpdate = true;
}

export function paintSelectActionDial(active: SelectAction): void {
  const W = selectActionDial.canvas.width;
  const H = selectActionDial.canvas.height;
  const ctx = selectActionDial.canvas.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);
  const cx = W / 2;
  const cy = H / 2;
  const outerR = W * 0.48;
  const innerR = W * 0.14;

  // Background disc.
  ctx.fillStyle = DIAL_GLASS;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR + 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(244, 180, 0, 0.85)";
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR + 4, 0, Math.PI * 2);
  ctx.stroke();

  // Two halves: right = DELETE (push right), left = MOVE (push left).
  const ACTIONS: {
    mode: SelectAction;
    start: number;
    end: number;
    center: number;
    label: string;
  }[] = [
    {
      mode: "delete",
      start: -Math.PI / 2,
      end: Math.PI / 2,
      center: 0,
      label: "DELETE",
    },
    {
      mode: "move",
      start: Math.PI / 2,
      end: (3 * Math.PI) / 2,
      center: Math.PI,
      label: "MOVE",
    },
  ];
  const sectionR = (outerR + innerR) / 2;
  const vertOffset = 70;
  const iconSize = 160;
  const labelFontPx = 56;
  // MOVE reads amber, DELETE reads red — same palette as the laser
  // tints for the corresponding gestures.
  const halfRGB = (mode: SelectAction): string =>
    mode === "move" ? "244, 180, 0" : "229, 70, 70";
  for (const a of ACTIONS) {
    const hot = a.mode === active;
    const rgb = halfRGB(a.mode);
    ctx.fillStyle = hot ? `rgba(${rgb}, 0.92)` : `rgba(${rgb}, 0.22)`;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, a.start, a.end);
    ctx.arc(cx, cy, innerR, a.end, a.start, true);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = `rgba(${rgb}, 0.85)`;
    ctx.lineWidth = 4;
    ctx.stroke();
    const secX = cx + Math.cos(a.center) * sectionR;
    const secY = cy + Math.sin(a.center) * sectionR;
    const iy = secY - vertOffset;
    if (a.mode === "move") paintMoveIcon(ctx, secX, iy, iconSize);
    else paintDeleteIcon(ctx, secX, iy, iconSize);
    const ly = secY + vertOffset;
    ctx.fillStyle = TEXT_LIGHT;
    ctx.font = glassFont(`600 ${labelFontPx}px`);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(a.label, secX, ly);
  }

  // Hub.
  ctx.fillStyle = DIAL_GLASS;
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(244, 180, 0, 0.85)";
  ctx.lineWidth = 4;
  ctx.stroke();

  selectActionDial.texture.needsUpdate = true;
}

const _dialCamPosTmp = new THREE.Vector3();
const _dialCamQuatTmp = new THREE.Quaternion();
const _dialOffsetTmp = new THREE.Vector3();
const _dialEyeLTmp = new THREE.Vector3();
const _dialEyeRTmp = new THREE.Vector3();
// Place `dial` in the user's view at a fixed camera-local offset. Both
// dials share the same offset since they're mutually exclusive — only one
// can be visible at a time. Anchors at the true head center (midpoint of
// the two eye cameras); three's XR union camera is side-biased on devices
// with asymmetric per-eye frustums (e.g. Vision Pro), which would pull the
// dial off-center.
export function positionDial(dial: Dial, cam: THREE.Camera): void {
  const eyes = (cam as THREE.ArrayCamera).cameras;
  if (eyes && eyes.length === 2) {
    _dialEyeLTmp.setFromMatrixPosition(eyes[0].matrixWorld);
    _dialEyeRTmp.setFromMatrixPosition(eyes[1].matrixWorld);
    _dialCamPosTmp.copy(_dialEyeLTmp).add(_dialEyeRTmp).multiplyScalar(0.5);
  } else {
    cam.getWorldPosition(_dialCamPosTmp);
  }
  cam.getWorldQuaternion(_dialCamQuatTmp);
  _dialOffsetTmp
    .set(0, -DIAL_DOWN, -DIAL_FORWARD)
    .applyQuaternion(_dialCamQuatTmp);
  dial.group.position.copy(_dialCamPosTmp).add(_dialOffsetTmp);
  dial.group.quaternion.copy(_dialCamQuatTmp);
}

// Clamp a raw (x, y) joystick reading to the unit disc. At full diagonal
// deflection raw values can hit (±1, ±1) which has magnitude √2; this
// keeps the dot inside the hub on diagonals.
export function clampToUnitDisc(
  x: number,
  y: number,
): { x: number; y: number } {
  const m = Math.hypot(x, y);
  const k = m > 1 ? 1 / m : 1;
  return { x: x * k, y: y * k };
}
